import { Select } from 'components/atoms/Form/Select';
import { SelectProps } from 'components/atoms/Form/Select/select.types';
import { isEmpty } from 'lodash';
import type { FC } from 'react';
import formatPeriodLabel from 'utils/formatPeriodLabel';
import useExpensesCategoriesPicker, {
  CategoryChangeParams,
  UseExpensesCategoriesPickerProps,
} from './useExpensesCategoriesPicker';

export type CategoriesPickerProps = {
  value?: { id: string; [key: string]: any };
  onChange: (category: any, params?: CategoryChangeParams) => void;
  name: string;
  error?: string;
  taxPeriod?: any;
  pickerController: ReturnType<typeof useExpensesCategoriesPicker>;
  highlightID?: string;
  placeholder?: string;
  isDisabled?: boolean;
  aiTooltip?: SelectProps['aiTooltip'];
};

export const CategoriesPickerControlled: FC<CategoriesPickerProps> = ({
  value: selectedCategory,
  onChange,
  taxPeriod,
  name,
  error,
  pickerController,
  placeholder,
  highlightID,
  isDisabled,
  aiTooltip,
}) => {
  const { api, query } = pickerController;
  const value = selectedCategory?.id;
  const dictCategory = value ? api.data.categoriesDict[value] : null;

  const { data, open } = api;
  const { isLoading, isError, refetch: retry } = query;

  const getLabel = () => {
    if (!selectedCategory) return null;
    const displayName =
      dictCategory?.displayName ||
      dictCategory?.title ||
      selectedCategory.displayName ||
      selectedCategory.title ||
      selectedCategory.id ||
      '-';
    if (!taxPeriod) return displayName;
    return `${displayName} - ${formatPeriodLabel(taxPeriod)}`;
  };

  return (
    <Select
      name={name}
      error={error}
      value={query.isLoading ? null : getLabel()}
      isLoading={isLoading}
      isDisabled={isLoading || isDisabled}
      isError={isError}
      retry={retry}
      onMenuOpen={() => {
        if (isEmpty(data.categoriesList)) return;
        open({ value, onChange });
      }}
      highlightID={highlightID}
      openMenuOnClick
      isSearchable={false}
      noOptionsMessage={() => null}
      placeholder={placeholder}
      aiTooltip={aiTooltip}
    />
  );
};

const CategoriesPicker: FC<
  UseExpensesCategoriesPickerProps &
    Omit<CategoriesPickerProps, 'pickerController'>
> = (props) => {
  const {
    defaultTaxPeriod,
    taxPeriod,
    tracing,
    tooltips,
    renderGridHeader,
    renderHeader,
    header,
    searchBarPlaceholder,
    aiTooltip,
  } = props;

  const controller = useExpensesCategoriesPicker({
    defaultTaxPeriod,
    taxPeriod,
    tracing,
    tooltips,
    renderGridHeader,
    renderHeader,
    header,
    searchBarPlaceholder,
  });

  const { name, value, onChange, error, highlightID, placeholder, isDisabled } =
    props;

  return (
    <>
      {controller.api.content}
      <CategoriesPickerControlled
        value={value}
        onChange={onChange}
        taxPeriod={taxPeriod}
        name={name}
        error={error}
        pickerController={controller}
        highlightID={highlightID}
        placeholder={placeholder}
        isDisabled={isDisabled}
        aiTooltip={aiTooltip}
      />
    </>
  );
};

export default CategoriesPicker;
